import type { Locale } from '@mfw/translations';

import { getLocalizedNameByLocale } from '@mfw/components';
import { config } from '@mfw/translations';

import type {
  UiEducationTranslation,
  ApiModuleTranslation,
  UiCreateModule,
  ApiModule,
  UiModule,
} from '../../schemas/modules.schema';

import { DEFAULT_CURRENCY } from '../../config/constants';

function nameFromTranslations(
  locale: Locale,
  translations?: ApiModuleTranslation[],
): string {
  if (!translations?.length) return '';
  return (
    translations.find((t) => t.locale === locale)?.name ||
    translations.find((t) => t.locale === config.defaultLocale)?.name ||
    translations[0]?.name ||
    ''
  );
}

function mapTranslations(
  translations: ApiModuleTranslation[],
): UiEducationTranslation[] {
  return (translations ?? []).map((t) => ({
    description: t.description ?? '',
    locale: [
      { label: getLocalizedNameByLocale(t.locale as Locale), value: t.locale },
    ],
    name: t.name,
  }));
}

export function mapModuleFromApi(module: ApiModule, locale: Locale): UiModule {
  return {
    id: module.id,
    authorId: module.author?.id,
    previewOnly: module.previewOnly,
    authorName: module.author?.name,
    coverSignedUrl: module.coverSignedUrl,
    coverSource: module.coverSource,
    coverType: module.coverType,
    createdAt: module.createdAt,
    documentSignedUrl: module.documentSignedUrl,
    documentSource: module.documentSource,
    documentType: module.documentType,
    episodes: (module.moduleEpisodes ?? [])
      .map((me) => ({
        episodeId: me.episodeId,
        name: nameFromTranslations(locale, me.episode?.translations),
        position: me.position,
        topics: (me.episode?.tags ?? [])
          .map((tag) => nameFromTranslations(locale, tag.translations))
          .filter(Boolean),
      }))
      .sort((a, b) => a.position - b.position),
    isDeleted: module.isDeleted ?? false,
    isMarketplace:
      module.configuration?.marketplace?.enabled ??
      module.isMarketplace ??
      false,
    isOrg: module.configuration?.isOrg ?? module.isOrg ?? true,
    marketplaceCurrency:
      module.configuration?.marketplace?.currency ?? DEFAULT_CURRENCY,
    marketplaceId: module.configuration?.marketplace?.id ?? null,
    marketplaceInLibrary: module.configuration?.marketplace?.inLibrary ?? false,
    marketplaceRestorable:
      module.configuration?.marketplace?.restorable ?? false,
    marketplaceLibraryId: module.configuration?.marketplace?.libraryId ?? null,
    marketplacePrice: module.configuration?.marketplace?.price ?? 0,
    organizationId: module.organizationId,
    sequentialUnlock: module.sequentialUnlock,
    stats: module.stats,
    translations: mapTranslations(module.translations),
    updatedAt: module.updatedAt,
  };
}

export function createModuleApiArgs(module: UiCreateModule) {
  return {
    configuration: {
      isOrg: module.isOrg ?? true,
      marketplace: {
        currency: module.marketplaceCurrency ?? DEFAULT_CURRENCY,
        enabled: Boolean(module.isMarketplace),
        price: module.marketplacePrice ?? 0,
      },
    },
    coverSource: module.coverSource ?? null,
    coverType: module.coverType ?? null,
    documentSource: module.documentSource ?? null,
    documentType: module.documentType ?? null,
    episodes: module.episodes,
    sequentialUnlock: module.sequentialUnlock ?? true,
    translations: (module.translations ?? []).map((t) => ({
      description: t.description,
      locale: t.locale?.[0]?.value,
      name: t.name,
    })),
  };
}
